import { kitchenKey } from '../hooks/useKitchenReady'
import { waitingQty } from './serving'
import type { KitchenTicket, Order, OrderLine } from '../types'

/**
 * "MASA DEĞİŞTİ" fişi (23 Eki 2026): adisyon taşınınca ya da birleşince,
 * tezgahta eski masa adıyla duran kağıt fiş yanlış masayı gösteriyor. Kasa
 * bekleyen ürünler için tezgah tezgah kısa bir fiş basıyor.
 *
 * Bekleyen: mutfakta açık fişte, "hazır" denmemiş ve garsonun henüz
 * götürmediği adet (Sipariş Takip). Mutfak ekranı olmayan kurumda fişler hiç
 * kapanmıyor; bir saatten eski fiş artık masaya gitmiş sayılıyor.
 */
export const MOVE_SLIP_MAX_AGE_MS = 60 * 60_000

export function moveSlipLines(
  tickets: KitchenTicket[],
  lines: OrderLine[],
  now = Date.now(),
): OrderLine[] {
  // Garsonun götürmediği adet, satır kimliğine göre. Götürülenler en eski
  // fişlerden düşüyor, o yüzden bekleyenler en yeni fişlerden sayılıyor.
  const waiting = new Map<string, number>()
  for (const l of lines) {
    const k = kitchenKey(l)
    waiting.set(k, (waiting.get(k) ?? 0) + waitingQty(l))
  }
  const sample = new Map(lines.map((l) => [kitchenKey(l), l]))

  const out: OrderLine[] = []
  const live = tickets
    .filter((t) => t.status === 'open' && now - t.createdAt <= MOVE_SLIP_MAX_AGE_MS)
    .sort((a, b) => b.createdAt - a.createdAt)
  for (const t of live) {
    for (const it of t.items) {
      if (it.status === 'ready') continue
      const left = waiting.get(it.key) ?? 0
      const qty = Math.min(it.qty, left)
      if (qty <= 0) continue
      waiting.set(it.key, left - qty)
      const base = sample.get(it.key)
      const prev = out.find((l) => l.station === t.station && kitchenKey(l) === it.key)
      if (prev) {
        prev.qty += qty
        continue
      }
      out.push({
        ...(base ?? ({ productId: it.key, unitPriceKurus: 0, lineTotalKurus: 0 } as OrderLine)),
        name: it.name,
        note: it.note ?? undefined,
        qty,
        station: t.station,
      })
    }
  }
  return out
}

/** Basılacak fiş; bekleyen ürün yoksa null. */
export function buildMoveSlip(order: Order, tickets: KitchenTicket[], now = Date.now()): Order | null {
  const moved = tickets.filter((t) => t.movedAt && t.movedFrom)
  if (moved.length === 0) return null
  const at = Math.max(...moved.map((t) => t.movedAt!))
  const batch = moved.filter((t) => t.movedAt === at)
  const lines = moveSlipLines(batch, order.lines, now)
  if (lines.length === 0) return null
  return {
    ...order,
    id: `${order.id}+move+${at}`,
    lines,
    itemCount: lines.reduce((n, l) => n + l.qty, 0),
    totalKurus: 0,
    move: { from: batch[0].movedFrom!, by: batch[0].movedBy, at },
  }
}
